import { useState } from 'react';
import { useReviewEvidence } from '@/api/evidence';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Combobox } from '@/components/shared/combobox';
import { useCoursesList, useCourseDetail, useLessonEvidence } from '@/api/instructor-evidence';
import { EvidenceCard } from './_components/evidence-card';

export function EvidenceReviewPage() {
  const [selectedCourseId, setSelectedCourseId] = useState<number>(0);
  const [selectedLessonId, setSelectedLessonId] = useState<number>(0);
  const [reviewing, setReviewing] = useState<{
    id: number;
    action: 'approved' | 'rejected';
  } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const { data: coursesRes, isLoading: loadingCourses, error: coursesError } = useCoursesList();
  const { data: courseDetail, isLoading: loadingDetail } = useCourseDetail(selectedCourseId);
  const {
    data: evidenceRes,
    isLoading: loadingEvidence,
    error: evidenceError,
  } = useLessonEvidence(selectedLessonId);

  const reviewMut = useReviewEvidence();
  const courses = coursesRes?.data ?? [];
  const evidence = evidenceRes?.data ?? [];

  const evidenceLessons =
    courseDetail?.sections?.flatMap((s) =>
      s.lessons.filter((l) => l.lesson_type === 'evidence_upload'),
    ) ?? [];

  const handleReview = () => {
    if (!reviewing) return;
    reviewMut.mutate(
      { id: reviewing.id, status: reviewing.action, review_notes: reviewNotes || undefined },
      {
        onSuccess: () => {
          setReviewing(null);
          setReviewNotes('');
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Revisar Evidencias</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Revisa las evidencias subidas por los empleados en tus cursos.
        </p>
      </div>

      {/* Selectors */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Curso</label>
          {loadingCourses ? (
            <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              Cargando cursos...
            </div>
          ) : coursesError ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Error cargando cursos
            </div>
          ) : (
            <Combobox
              value={selectedCourseId ? String(selectedCourseId) : ''}
              onChange={(v) => {
                setSelectedCourseId(v ? Number(v) : 0);
                setSelectedLessonId(0);
              }}
              options={courses.map((c) => ({ value: String(c.id), label: c.title }))}
              placeholder="Seleccionar curso..."
              clearable
            />
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Leccion</label>
          <Combobox
            value={selectedLessonId ? String(selectedLessonId) : ''}
            onChange={(v) => setSelectedLessonId(v ? Number(v) : 0)}
            options={evidenceLessons.map((l) => ({ value: String(l.id), label: l.title }))}
            placeholder={
              !selectedCourseId
                ? 'Selecciona un curso primero'
                : loadingDetail
                  ? 'Cargando lecciones...'
                  : evidenceLessons.length === 0
                    ? 'Sin lecciones de evidencia'
                    : 'Seleccionar lección...'
            }
            disabled={!selectedCourseId || loadingDetail}
            clearable
          />
        </div>
      </div>

      {/* Evidence list */}
      {selectedLessonId === 0 ? (
        <EmptyState
          title="Selecciona una lección"
          description="Elige un curso y una lección de tipo evidencia para ver las entregas."
        />
      ) : loadingEvidence ? (
        <LoadingSpinner />
      ) : evidenceError ? (
        <ErrorMessage message="No se pudieron cargar las evidencias." />
      ) : evidence.length === 0 ? (
        <EmptyState
          title="Sin evidencias"
          description="Ningún empleado ha subido evidencias para esta lección."
        />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {evidence.length} evidencia{evidence.length !== 1 && 's'} encontrada
            {evidence.length !== 1 && 's'}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {evidence.map((ev) => (
              <EvidenceCard
                key={ev.id}
                ev={ev}
                onReview={(action) => setReviewing({ id: ev.id, action })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Review confirmation dialog */}
      {reviewing && (
        <ConfirmDialog
          title={reviewing.action === 'approved' ? 'Aprobar evidencia' : 'Rechazar evidencia'}
          message={
            <div className="space-y-3">
              <p>
                {reviewing.action === 'approved'
                  ? 'La evidencia sera marcada como aprobada.'
                  : 'La evidencia sera marcada como rechazada. El empleado debera subir una nueva.'}
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Notas (opcional)
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Agregar comentarios sobre la revisión..."
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  rows={3}
                />
              </div>
            </div>
          }
          confirmLabel={reviewing.action === 'approved' ? 'Aprobar' : 'Rechazar'}
          variant={reviewing.action === 'approved' ? 'default' : 'danger'}
          isPending={reviewMut.isPending}
          onConfirm={handleReview}
          onCancel={() => {
            setReviewing(null);
            setReviewNotes('');
          }}
        />
      )}
    </div>
  );
}
